import type { InferContractRouterOutputs } from '@orpc/contract'
import DataLoader from 'dataloader'
import type { BaseContract } from 'pixai-contract/api/orpc'
import { contractPublicClient } from './contract'

export type BatchDecoration =
  InferContractRouterOutputs<BaseContract>['decoration']['batch']['data'][number]

/** Server caps /v2/decoration/batch at 50 ids per request. */
const CHUNK_LIMIT = 50

/**
 * DataLoader `batchLoadFn` for /v2/decoration/batch. Exported for unit testing.
 *
 * - Dedupes ids across the input batch (the API doesn't repeat results) and
 *   fires a single RPC. DataLoader's `maxBatchSize` chunks oversized batches
 *   for us, so callers can `loader.load(id)` freely without thinking about
 *   the 50-id cap.
 * - Returns an output array of the *same length and order* as `keys`. Ids the
 *   server omits resolve to `null` at their input slot.
 * - On RPC failure, the rejection propagates to all queued callers — React
 *   Query then marks those queries as errored and retries per its own
 *   policy (no negative-caching of network failures).
 */
export async function batchLoadDecorations(
  keys: readonly string[],
): Promise<Array<BatchDecoration | null>> {
  const ids = [...new Set(keys)]
  const { data } = await contractPublicClient.decoration.batch({ ids })
  const found = new Map<string, BatchDecoration>()
  for (const item of data) {
    found.set(item.id, item)
  }
  return keys.map(id => found.get(id) ?? null)
}

/**
 * Coalesces concurrent /v2/decoration/batch hydrations across the page.
 * DataLoader collects every `loader.load(...)` call within a microtask,
 * dispatches them as one `batchLoadFn` invocation, and resolves each caller
 * with its own item.
 *
 * `cache: false` is intentional: React Query owns per-id caching under
 * `['decorationItem', id]` (see `useDecorations`). Enabling DataLoader's
 * cache would shadow RQ refetches/invalidations — when RQ explicitly
 * invalidates a query, the next queryFn → `loadDecoration` would return a
 * stale cached Promise instead of re-fetching. So this layer is purely a
 * batcher; the in-tick same-key dedup that DataLoader's cache would
 * otherwise provide is already covered by RQ's per-key in-flight dedup.
 *
 * Module-level singleton: each browser tab is its own module instance, and
 * the call sites (`UserAvatar`, `ArtworkFrameForArtworkCard` via
 * `useAuthorActiveDecorations`) don't fire during SSR.
 */
const decorationLoader = new DataLoader<string, BatchDecoration | null, string>(
  batchLoadDecorations,
  {
    cache: false,
    maxBatchSize: CHUNK_LIMIT,
    cacheKeyFn: id => id,
  },
)

/** Returns `null` when the server omits an id (unknown id). */
export function loadDecoration(id: string): Promise<BatchDecoration | null> {
  return decorationLoader.load(id)
}
